import React, { useMemo, useState } from 'react';
import { useTranslation } from '../../utils/i18nStub';

const regions = [
  { value: 'riyadh', label: 'منطقة الرياض' },
  { value: 'makkah', label: 'منطقة مكة المكرمة' },
  { value: 'madinah', label: 'منطقة المدينة المنورة' },
  { value: 'sharqiah', label: 'المنطقة الشرقية' },
  { value: 'qassim', label: 'منطقة القصيم' },
  { value: 'asir', label: 'منطقة عسير' },
  { value: 'tabuk', label: 'منطقة تبوك' },
];

const citiesByRegion = {
  riyadh: ['الرياض', 'الخرج', 'الدوادمي', 'وادي الدواسر'],
  makkah: ['مكة المكرمة', 'جدة', 'الطائف'],
  madinah: ['المدينة المنورة', 'العلا', 'ينبع'],
  sharqiah: ['الدمام', 'الخبر', 'الجبيل'],
};

const centers = ['المركز الرئيسي', 'فرع الشمال', 'فرع الجنوب', 'فرع الشرق', 'فرع الغرب'];
const classificationOptions = [
  { value: 'جامع', label: 'جامع' },
  { value: 'مسجد جامع', label: 'مسجد جامع' },
  { value: 'مسجد', label: 'مسجد' },
  { value: 'مصلى', label: 'مصلى' },
];

const EmployeeJameData = () => {
  const { t } = useTranslation('jameTab');
  const [form, setForm] = useState({
    name: '',
    number: '',
    classification: '',
    region: '',
    city: '',
    center: '',
    district: '',
    street: '',
    fullAddress: '',
    imamName: '',
    khateebName: '',
    mapLink: '',
  });

  const availableCities = useMemo(() => {
    if (!form.region) return [];
    return citiesByRegion[form.region] || [];
  }, [form.region]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState({ ...form });

  const startEdit = () => {
    setSnapshot({ ...form });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setForm(snapshot);
    setIsEditing(false);
  };

  const handleSave = () => {
    setSnapshot({ ...form });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6 text-right" dir="rtl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {t('jameTab.title', 'بيانات الجامع')}
        </h2>
        {!isEditing ? (
          <button
            type="button"
            onClick={startEdit}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors"
            >
              {t('save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.name', 'اسم الجامع')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(event) => handleChange('name', event.target.value)}
              placeholder={t('jameTab.name_placeholder', 'مثال: جامع الملك فهد')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.number', 'الرقم التسلسلي للجامع')}</label>
            <input
              type="text"
              value={form.number}
              onChange={(event) => handleChange('number', event.target.value)}
              placeholder="654321"
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.classification', 'تصنيف الجامع')}</label>
            <select
              value={form.classification}
              onChange={(event) => handleChange('classification', event.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              disabled={!isEditing}
            >
              <option value="">{t('jameTab.select_classification', 'اختر التصنيف')}</option>
              {classificationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.region', 'المنطقة')}</label>
            <select
              value={form.region}
              onChange={(event) => handleChange('region', event.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              disabled={!isEditing}
            >
              <option value="">{t('jameTab.select_region', 'اختر المنطقة')}</option>
              {regions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.city', 'المدينة')}</label>
            <select
              value={form.city}
              onChange={(event) => handleChange('city', event.target.value)}
              disabled={!isEditing || !form.region}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">{t('jameTab.select_city', 'اختر المدينة')}</option>
              {availableCities.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.center', 'المركز / الفرع')}</label>
            <select
              value={form.center}
              onChange={(event) => handleChange('center', event.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              disabled={!isEditing}
            >
              <option value="">{t('jameTab.select_center', 'اختر المركز')}</option>
              {centers.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.district', 'الحي')}</label>
            <input
              type="text"
              value={form.district}
              onChange={(event) => handleChange('district', event.target.value)}
              placeholder={t('jameTab.district_placeholder', 'مثال: حي الروضة')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.street', 'الشارع')}</label>
            <input
              type="text"
              value={form.street}
              onChange={(event) => handleChange('street', event.target.value)}
              placeholder={t('jameTab.street_placeholder', 'وصف الشارع أو المعلم الأبرز')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="md:col-span-2 flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.fullAddress', 'العنوان الكامل')}</label>
            <textarea
              value={form.fullAddress}
              onChange={(event) => handleChange('fullAddress', event.target.value)}
              rows={3}
              placeholder={t('jameTab.fullAddress_placeholder', 'مثال: حي الروضة، شارع الأمير محمد بن عبدالعزيز، بالقرب من الحديقة العامة')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="md:col-span-2 flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">{t('jameTab.mapLink', 'رابط الخريطة (اختياري)')}</label>
            <input
              type="url"
              value={form.mapLink}
              onChange={(event) => handleChange('mapLink', event.target.value)}
              placeholder="https://maps.google.com/..."
              className="rounded-xl border border-gray-300 px-4 py-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
              dir="ltr"
            />
          </div>
        </div>

        {isEditing && (
          <footer className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('jameTab.cancel', 'إلغاء')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-sa-600 text-white px-5 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors"
            >
              {t('jameTab.save', 'حفظ البيانات')}
            </button>
          </footer>
        )}
      </section>
    </div>
  );
};

export default EmployeeJameData;
